/**
 * defaultTemplates — the built-in, on-brand default Design doc per platform.
 *
 * Used in two places:
 *   - getConfig() merges a default when no template is saved (mirrors
 *     DEFAULT_NOTIFICATION), so the system always has a renderable design.
 *   - the editor seeds a new template from buildDefaultDesign(platform).
 *
 * On-brand to DESIGN.md ("The Trusted Stable"): full-bleed listing photo, a navy
 * (#1a2b49) headline bound to the title, a green (#1e9f55) price badge. The text
 * face is BRAND_FONT_FAMILY (the single brand font both the server renderer and
 * the client editor register — see brandFont.js).
 *
 * ESM module, no node-only deps — client-safe (the duck imports it directly).
 *
 * @module render/defaultTemplates
 */
import { CANVAS_PRESETS } from './platformSpecs.js';
import { BRAND_FONT_FAMILY, FILES } from './brandFont.js';

const GREEN = '#1e9f55';

// Brand graphics baked into the default feed design (resolved by
// DesignRenderer.resolveAssetSrc server-side, loaded by URL on the client).
const PIN_SRC = '/static/social/pin-white.svg';
const LOGO_SRC = '/static/social/horsedeal-logomark-white.svg';

// The fonts every default doc declares; the server resolves these files against
// render/fonts/ and the editor against /static/fonts/ — ONE source (brandFont.js)
// so both ends register the exact same face (preview == published at the glyph
// level). Shipping both weights so a bold headline name-matches.
const FONTS = FILES;

/**
 * The on-brand layer stack ("The Trusted Stable"): a full-bleed listing photo, a
 * translucent navy scrim, a white headline bound to the title, a green price badge.
 * ONE builder so the legacy per-platform default AND the platform-free presets share
 * the exact same composition — they differ only in canvas size + headline ratio.
 *
 * @param {{width:number,height:number,headlineRatio:number}} dims
 * @returns {object[]} the four brand layers, sized to the canvas
 */
const brandLayers = ({ width, height, headlineRatio }) => {
    const pad = Math.round(width * 0.05);
    // A translucent navy scrim along the bottom so the headline stays legible over
    // any photo (deterministic legibility, not color-only).
    const scrimHeight = Math.round(height * 0.38);
    const headlineSize = Math.round(width * headlineRatio);
    const badgeFont = Math.round(width * 0.04);
    // Location row (pin + Standort) sits at the top of the scrim; the logo sits
    // top-right — so a feed post carries location + brand out of the box like the
    // Story. All ratio-based, so they scale across every seed canvas size.
    const rowH = Math.round(width * 0.045);
    const locFont = Math.round(width * 0.032);
    const gap = Math.round(width * 0.015);
    const logoW = Math.round(width * 0.09);
    const locY = height - scrimHeight + Math.round(scrimHeight * 0.08);
    return [
        {
            type: 'image',
            id: 'photo',
            name: 'Inseratfoto',
            x: 0,
            y: 0,
            width,
            height,
            rotation: 0,
            fit: 'cover',
            bind: 'context.imageUrls[0]',
        },
        {
            type: 'shape',
            id: 'scrim',
            name: 'Abdunkelung',
            shape: 'rect',
            x: 0,
            y: height - scrimHeight,
            width,
            height: scrimHeight,
            rotation: 0,
            fill: 'rgba(26,43,73,0.66)',
        },
        {
            type: 'image',
            id: 'pin',
            name: 'Standort-Pin',
            x: pad,
            y: locY,
            width: rowH,
            height: rowH,
            rotation: 0,
            fit: 'contain',
            src: PIN_SRC,
        },
        {
            type: 'text',
            id: 'location',
            name: 'Standort',
            x: pad + rowH + gap,
            y: locY,
            width: width - pad * 2 - rowH - gap,
            height: rowH,
            rotation: 0,
            bind: 'context.location',
            fontFamily: BRAND_FONT_FAMILY,
            fontSize: locFont,
            fontWeight: 400,
            fill: '#ffffff',
            align: 'left',
            lineHeight: 1.1,
            wrap: 'none',
            ellipsis: true,
        },
        {
            type: 'text',
            id: 'headline',
            name: 'Titel',
            x: pad,
            y: height - scrimHeight + Math.round(scrimHeight * 0.34),
            width: width - pad * 2,
            height: Math.round(scrimHeight * 0.46),
            rotation: 0,
            bind: 'context.title',
            fontFamily: BRAND_FONT_FAMILY,
            fontSize: headlineSize,
            fontWeight: 700,
            fontStyle: 'bold',
            fill: '#ffffff',
            align: 'left',
            lineHeight: 1.15,
            wrap: 'word',
            ellipsis: true,
        },
        {
            type: 'image',
            id: 'logo',
            name: 'HorseDeal Logo',
            x: width - pad - logoW,
            y: pad,
            width: logoW,
            height: logoW,
            rotation: 0,
            fit: 'contain',
            src: LOGO_SRC,
        },
        {
            type: 'badge',
            id: 'price',
            name: 'Preis',
            x: pad,
            y: pad,
            width: 0,
            height: 0,
            rotation: 0,
            bind: 'context.price',
            fill: GREEN,
            textFill: '#ffffff',
            cornerRadius: 5,
            paddingX: Math.round(badgeFont * 0.7),
            paddingY: Math.round(badgeFont * 0.4),
            fontSize: badgeFont,
            fontFamily: BRAND_FONT_FAMILY,
            fontWeight: 700,
        },
    ];
};

/**
 * buildDefaultPreset — the platform-FREE on-brand design doc at a named canvas size
 * (or arbitrary dims). This is the design a fresh preset starts from: it owns its
 * own canvas and carries NO platform. A taller-than-wide canvas gets the larger
 * headline ratio (matching the old Instagram-portrait look).
 *
 * @param {string|{width:number,height:number,label?:string}} [size] a CANVAS_PRESETS
 *   id ('portrait'|'square'|'landscape'|'story') or an explicit {width,height}
 * @returns {object} a valid, platform-free design doc
 */
const buildDefaultPreset = (size = 'portrait') => {
    const dims = typeof size === 'string' ? CANVAS_PRESETS.find(c => c.id === size) : size;
    if (!dims || !dims.width || !dims.height) {
        throw new Error(`Unknown canvas size "${JSON.stringify(size)}"`);
    }
    const { width, height } = dims;
    const doc = {
        version: 1,
        width,
        height,
        dpr: 2,
        quality: 0.9,
        background: '#ffffff',
        fonts: FONTS,
        layers: brandLayers({ width, height, headlineRatio: height >= width ? 0.06 : 0.05 }),
    };
    if (dims.label) doc.name = dims.label;
    return doc;
};

/**
 * buildBarePhotoDoc — the MINIMAL design doc for promoting a bare-photo passthrough
 * (a `trigger.image → platform_exit` channel that publishes the raw photo with no
 * composition) into the one crop engine. It is a single full-bleed listing photo —
 * no scrim/headline/badge — so the reviewer can pan/zoom-crop it exactly like any
 * other image layer, then re-render through the same DesignRenderer. The photo binds
 * to context.imageUrls[0]; rerenderChannel resolves that against the channel's own
 * source, so it round-trips losslessly.
 *
 * @param {string|{width:number,height:number}} [size] a CANVAS_PRESETS id or {width,height}
 * @returns {object} a valid, platform-free design doc (passes validateDesignDoc)
 */
const buildBarePhotoDoc = (size = 'landscape') => {
    const dims = typeof size === 'string' ? CANVAS_PRESETS.find(c => c.id === size) : size;
    if (!dims || !dims.width || !dims.height) {
        throw new Error(`Unknown canvas size "${JSON.stringify(size)}"`);
    }
    const { width, height } = dims;
    return {
        version: 1,
        width,
        height,
        dpr: 2,
        quality: 0.9,
        background: '#ffffff',
        fonts: [],
        layers: [
            {
                type: 'image',
                id: 'photo',
                name: 'Inseratfoto',
                x: 0,
                y: 0,
                width,
                height,
                rotation: 0,
                fit: 'cover',
                bind: 'context.imageUrls[0]',
                focalX: 0.5,
                focalY: 0.5,
                zoom: 1,
            },
        ],
    };
};

// ---- Instagram "Highlights" Story preset (9:16) ----

// The exact brand backdrop the team designed: a purple swirl with the
// "Highlights" wordmark (top) and the horse-in-heart logo (bottom) BAKED IN. It
// ships in public/static/ (and is copied to build/ on yarn build); the client
// loads the /static URL natively and the server renderer resolves it to the file
// (DesignRenderer.resolveAssetSrc), so the one src renders on both ends.
const STORY_BG_SRC = '/static/social/story-highlights-bg.png';
const STORY_PIN_SRC = '/static/social/pin-white.svg';
// The CTA glyphs that flank "ZUM PROFIL". Color emoji (🔗 💜) can't render in the
// server's node-canvas/text stack, so they ship as their own vector assets (like
// the pin): a colorful gradient chain-link and a vivid-purple heart, matching the
// Storrito reference exactly on BOTH the server render and the client preview.
const STORY_LINK_SRC = '/static/social/link-gradient.svg';
const STORY_HEART_SRC = '/static/social/heart-purple.svg';
const PURPLE = '#8f2593';
// The CTA label is pure black in the reference (not brand purple) — match it.
const CTA_INK = '#111111';

// The Story's ONE vertical rhythm: the listing photo's frame is the card's visual
// content column, so the location row above it must start after the pin and end
// FLUSH with the photo's right edge. Deriving the text box from these keeps the
// truncation point (layerToKonva wrap:'none' + ellipsis, same math server + client)
// inside the photo column — a long "9213 Hauptwil-Gottshaus" ellipsises at the
// photo edge instead of 90px past it.
const STORY_PHOTO_X = 130;
const STORY_PHOTO_W = 820;
const STORY_LOCATION_X = 330;
const STORY_LOCATION_W = STORY_PHOTO_X + STORY_PHOTO_W - STORY_LOCATION_X;

/**
 * buildStoryHighlightsDoc — the bespoke 9:16 Instagram-Story design. The swirl,
 * the "Highlights" headline, and the logo are part of the baked background asset,
 * so this overlays ONLY the dynamic content: the location row (white pin + the
 * "PLZ Ort" string bound to context.location), the listing photo in a rounded
 * frame, and a white "ZUM PROFIL" call-to-action pill with a colorful gradient
 * link glyph and a purple heart flanking black label text (the Storrito reference).
 *
 * Positions are a deliberate starting layout (1080x1920 logical, dpr:2); they are
 * fully editable in the Konva editor / reviewer, WYSIWYG. `background` is brand
 * purple so the card degrades to a solid brand backdrop if the asset ever fails
 * to load (the bg image layer is decorative — requiredImage:false).
 *
 * @returns {object} a valid, platform-free 9:16 design doc (passes validateDesignDoc)
 */
const buildStoryHighlightsDoc = () => ({
    version: 1,
    width: 1080,
    height: 1920,
    dpr: 2,
    quality: 0.9,
    name: 'Highlights Story',
    background: PURPLE,
    fonts: FONTS,
    layers: [
        {
            type: 'image',
            id: 'bg',
            name: 'Hintergrund',
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            rotation: 0,
            fit: 'cover',
            src: STORY_BG_SRC,
        },
        {
            type: 'image',
            id: 'pin',
            name: 'Standort-Symbol',
            x: 243,
            y: 297,
            width: 68,
            height: 68,
            rotation: 0,
            fit: 'contain',
            src: STORY_PIN_SRC,
        },
        {
            type: 'text',
            id: 'location',
            name: 'Standort',
            x: STORY_LOCATION_X,
            y: 293,
            width: STORY_LOCATION_W,
            height: 80,
            rotation: 0,
            bind: 'context.location',
            fontFamily: BRAND_FONT_FAMILY,
            fontSize: 72,
            fontWeight: 400,
            fill: '#ffffff',
            align: 'left',
            verticalAlign: 'middle',
            lineHeight: 1.1,
            wrap: 'none',
            ellipsis: true,
        },
        {
            type: 'image',
            id: 'photo',
            name: 'Inseratfoto',
            x: STORY_PHOTO_X,
            y: 462,
            width: STORY_PHOTO_W,
            height: 1028,
            rotation: 0,
            fit: 'cover',
            cornerRadius: 28,
            // VARIABLE layer: the centred middle photo is filled by the design node's
            // wired `image` input (trigger.image), arranged once into this frame and
            // saved here, so every post crops into exactly this box. (Was a
            // context.imageUrls[0] bind; now the unified input-layer model.)
            source: { kind: 'input', as: 'image' },
            focalX: 0.5,
            focalY: 0.5,
            zoom: 1,
        },
        // The CTA is built from explicit layers (not the auto-sizing `badge`) so the
        // two flanking glyphs sit INSIDE the white pill at fixed insets and the label
        // stays centred — pixel-matching the Storrito reference. Stacking order:
        // white rounded rect → link glyph (left) → centred label → heart (right).
        {
            type: 'shape',
            id: 'cta-bg',
            name: 'Button',
            shape: 'rect',
            x: 243,
            y: 1503,
            width: 595,
            height: 126,
            rotation: 0,
            fill: '#ffffff',
            cornerRadius: 24,
            // VARIABLE link slot: the design node's wired `link` input (the Bitly
            // node) fills this layer's href. On an IG Story the publish step strips the
            // drawn cta-* layers and overlays a native, tappable <insta-link> at THIS
            // box (its own geometry drives the sticker position).
            source: { kind: 'input', as: 'link' },
        },
        {
            type: 'image',
            id: 'cta-link',
            name: 'Link-Symbol',
            x: 276,
            y: 1534,
            width: 64,
            height: 64,
            rotation: 0,
            fit: 'contain',
            src: STORY_LINK_SRC,
        },
        {
            type: 'text',
            id: 'cta-text',
            name: 'Button-Text',
            x: 243,
            y: 1503,
            width: 595,
            height: 126,
            rotation: 0,
            text: 'ZUM PROFIL',
            fontFamily: BRAND_FONT_FAMILY,
            fontSize: 54,
            fontWeight: 700,
            fill: CTA_INK,
            align: 'center',
            verticalAlign: 'middle',
            lineHeight: 1,
            wrap: 'none',
            ellipsis: false,
        },
        {
            type: 'image',
            id: 'cta-heart',
            name: 'Herz-Symbol',
            x: 766,
            y: 1534,
            width: 70,
            height: 64,
            rotation: 0,
            fit: 'contain',
            src: STORY_HEART_SRC,
        },
    ],
});

export { buildDefaultPreset, buildBarePhotoDoc, buildStoryHighlightsDoc };
